import { useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { MESSAGE_VARIABLES, MESSAGE_VARIABLE_LABELS, fillVariables, type MessageVariable } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { TIMELINE_KEY, communicationApi, useComposePreview, useComposeTemplates } from '../api/communication-api';
import styles from '../Communication.module.css';

export interface ComposeDraft {
  readonly templateKey: string | null;
  readonly subject: string;
  readonly body: string;
}

/**
 * COMM-03 — "templated email … with variables ({{first_name}}, {{job_title}}, {{interview_time}})"
 * (E13). With one person it previews the message filled in for them; for a bulk send (F15.03)
 * the variables stay as written and the server fills them for each recipient.
 */
export function ComposePanel({
  target,
  recipientsLabel,
  onSend,
  onDone,
  onCancel,
}: {
  /** One person: the message goes straight from here. Absent for a bulk send. */
  readonly target?: { readonly applicationId: string | null; readonly candidateId: string | null };
  /** A bulk send's "To" line, e.g. "12 candidates". */
  readonly recipientsLabel?: string;
  /** A bulk send hands the draft back to its caller. */
  readonly onSend?: (draft: ComposeDraft) => Promise<void>;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const templates = useComposeTemplates(true);
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const preview = useComposePreview(target ?? { applicationId: null, candidateId: null }, null, target !== undefined);

  const chooseTemplate = (key: string): void => {
    const template = templates.data?.find((row) => row.key === key);
    setTemplateKey(template ? template.key : null);
    if (template) {
      setSubject(template.subject);
      setBody(template.bodyText);
    }
  };

  const insert = (name: MessageVariable): void => {
    const token = `{{${name}}}`;
    const field = bodyRef.current;
    if (!field) {
      setBody((current) => `${current}${token}`);
      return;
    }
    const start = field.selectionStart;
    const end = field.selectionEnd;
    setBody((current) => `${current.slice(0, start)}${token}${current.slice(end)}`);
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const send = async (): Promise<void> => {
    if (subject.trim() === '' || body.trim() === '') {
      show({ tone: 'warning', message: 'Write a subject and a message' });
      return;
    }
    setIsSending(true);
    try {
      if (onSend) {
        await onSend({ templateKey, subject, body });
      } else if (target) {
        const sent = await communicationApi.send({ ...target, templateKey, subject, body });
        await queryClient.invalidateQueries({ queryKey: TIMELINE_KEY });
        show({ tone: 'success', message: 'Message sent', detail: `To ${sent.to}. It is on the timeline.` });
      }
      onDone();
    } catch (cause) {
      show({ tone: 'danger', message: "The message wasn't sent", detail: errorMessage(cause) });
    } finally {
      setIsSending(false);
    }
  };

  const values = preview.data?.values;
  return (
    <form
      className={styles.compose}
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void send();
        }
      }}
    >
      <div className={styles.composeBody}>
      <p className={styles.meta}>
        To {recipientsLabel ?? (preview.data ? `${preview.data.candidateName}${preview.data.to ? ` <${preview.data.to}>` : ' — no email address on file'}` : '…')}. Sent by email.
      </p>
      {(templates.data?.length ?? 0) > 0 && (
        <Field label="Template" isOptional>
          {({ inputId }) => (
            <Select id={inputId} label="Template" value={templateKey} placeholder="Write it yourself" options={(templates.data ?? []).map((row) => ({ value: row.key, label: row.name }))} onChange={chooseTemplate} />
          )}
        </Field>
      )}
      <Field label="Subject" isRequired>
        {({ inputId }) => <Input id={inputId} value={subject} maxLength={200} autoFocus onChange={(event) => setSubject(event.target.value)} />}
      </Field>
      <Field label="Message" isRequired hint="Insert a variable and it is filled in for each person.">
        {({ inputId, describedBy }) => <Textarea id={inputId} ref={bodyRef} aria-describedby={describedBy} value={body} rows={8} maxLength={10000} onChange={(event) => setBody(event.target.value)} />}
      </Field>
      <div className={styles.variables} aria-label="Variables">
        {MESSAGE_VARIABLES.map((name) => (
          <Button key={name} variant="ghost" size="sm" onClick={() => insert(name)}>
            {MESSAGE_VARIABLE_LABELS[name]}
          </Button>
        ))}
      </div>
      {values && body.trim() !== '' && (
        <pre className={styles.preview} aria-label="How it reads">
          {fillVariables(`${subject}\n\n${body}`, values)}
        </pre>
      )}
      </div>
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" icon={Send} isPending={isSending} disabled={target !== undefined && preview.data !== undefined && preview.data.to === null}>
          Send
        </Button>
      </div>
    </form>
  );
}
